/**
 * Service worker registration and update lifecycle.
 *
 * The worker deliberately does NOT call skipWaiting() on install (see
 * client/public/service-worker.js). A new version installs, parks in
 * `waiting`, and stays there until this module tells it to take over. That is
 * the safe order for an app that code-splits per route: activating immediately
 * deletes the caches holding the chunks the current page has not requested yet,
 * so a lazily-imported route would fail with a ChunkLoadError mid-session.
 *
 * Something therefore has to take the update, or it never lands. Two things do:
 *
 *   · the update card (components/UpdatePrompt.tsx), via `onUpdateReady`, when
 *     an update is found while someone is working; and
 *   · a RESUME after at least 30 seconds away, which applies it without asking
 *     (see `mayApplyWithoutAsking` for where it never does). An installed app
 *     on a phone is suspended and resumed, never reloaded — no `load`, no
 *     navigation — so a resume is the only moment it can ever pick up a
 *     deploy, and nobody is mid-keystroke in an app they have just come back
 *     to. Five consecutive PropPay deploys reached nobody before this existed.
 *
 * ── A PARKED WORKER IS NOT THE SAME THING AS A NEW VERSION ──
 *
 * `registration.waiting` being non-null used to be the whole test, so anything
 * that parked a worker announced itself as an update — and a worker parks and
 * STAYS parked until something activates it. Between 2026-08-27 and 2026-09-08
 * the worker did not change once across thirty-seven deploys, and users were
 * still told a new version was ready. So both workers are ASKED which version
 * they are, over a MessageChannel:
 *
 *   parked ahead of running    a real update              → offer it
 *   parked equals running      a zombie                   → activate it silently
 *   parked behind running      a stale copy of the script → leave it, say nothing
 *   nobody answers             both predate v11           → say nothing
 *
 * Versions are `v<generation>-<build id>` since the build id started being
 * stamped from a hash of the build (scripts/stamp-sw.mjs). The generation is
 * compared as a number — "v9" > "v10" as a string would read every downgrade
 * as an upgrade — and is what lets a stamped v12 announce itself over the
 * hand-numbered v11 it replaces. Two builds of the same generation have no
 * order (a hash is not a counter), so different means "the one the server has
 * now"; the worker script is served no-cache (vercel.json), which is what keeps
 * a stale copy from ever being the one that parks.
 */

type UpdateHandler = (version: string) => void;

/** The parked worker `applyUpdate()` will swap to. */
let waitingWorker: ServiceWorker | null = null;
/** The worker already assessed, so a re-check does not re-run the handshake. */
let assessed: ServiceWorker | null = null;
/** Set only once a parked worker has been established to be genuinely newer. */
let pendingVersion: string | null = null;
let updateHandler: UpdateHandler | null = null;
/** Guards against the reload loop described in the controllerchange handler. */
let reloading = false;
/** Set while activating a same-version zombie; that swap must not reload. */
let silentSwap = false;
/**
 * Whether a worker already controlled this page when it loaded. On a FIRST
 * install the new worker's `clients.claim()` fires `controllerchange` too —
 * an event that means "a worker arrived", not "a new build was taken", and
 * reloading on it threw away whatever the reader had just done: a half-typed
 * sign-in on their very first visit.
 */
let hadController = false;

/** Test seam: `null` means decide from the build (see serviceWorkerEnabled). */
let forceEnabled: boolean | null = null;

/**
 * A worker that does not answer is older than v11, not broken — so the
 * handshake resolves rather than rejects, and waits only long enough for a
 * running worker to reply to a message it handles synchronously.
 */
let versionReplyTimeoutMs = 2_000;

/* ── Resume ──────────────────────────────────────────────────────────── */

/** Away at least this long, and an update may apply itself on return. */
export const AWAY_LONG_ENOUGH_MS = 30_000;
/**
 * How long a resume stays "a resume". `awayMs` is a fact about one moment; a
 * resume that found no update must not leave it set, or a deploy landing
 * hours later — with the reader mid-keystroke — would read as "they have just
 * come back, take it".
 */
const RESUME_FRESH_MS = 60_000;
/** iOS fires visibility events liberally, and every check is a network request. */
export const RESUME_CHECK_THROTTLE_MS = 20_000;
/** How often an app left open and on screen asks whether there is a new version. */
export const OPEN_CHECK_MS = 15 * 60_000;

let hiddenSince = 0;
let awayMs = 0;
let resumedAt = 0;

/**
 * Pages where an update must never apply itself, whatever the timing:
 *
 *   /quote/, /register/  a builder's customer, possibly mid-signature on the
 *                        acceptance pad, or on the registration form embedded
 *                        in a builder's own site;
 *   /roadmap/            a public page sent to people outside the company;
 *   sign-in and reset    leaving the app to fetch a code from Mail IS the flow.
 */
const NEVER_SELF_APPLY = /^\/(?:quote|register|roadmap|worksheet-response|sign)\/|^\/(?:login|forgot-password|reset-password)(?:\/|$)/;

/**
 * Whether an update may be applied WITHOUT asking — which reloads the page.
 *
 * Path alone cannot see every screen where a reload costs something: the 2FA
 * step renders at whatever path the reader was going to, and a dialog can hold
 * a half-finished form on any page. So the screen is looked at too: a password
 * or one-time-code field, an open dialog (the nudge cards excepted), or focus
 * in an editable field all mean "ask instead".
 */
export function mayApplyWithoutAsking(
  where: Pick<Location, "pathname"> = window.location,
  doc: Document = document,
): boolean {
  if (NEVER_SELF_APPLY.test(where.pathname)) return false;
  if (
    doc.querySelector(
      'input[type="password"], input[autocomplete="one-time-code"], [role="alertdialog"], [role="dialog"]:not([data-nudge])',
    )
  ) {
    return false;
  }
  const active = doc.activeElement as HTMLElement | null;
  if (active && (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) return false;
  return true;
}

function justResumed(): boolean {
  return awayMs >= AWAY_LONG_ENOUGH_MS && Date.now() - resumedAt < RESUME_FRESH_MS;
}

/* ── The handshake ───────────────────────────────────────────────────── */

function askVersion(worker: ServiceWorker | null): Promise<string | null> {
  if (!worker) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    let channel: MessageChannel | null = null;
    const done = (version: string | null) => {
      if (settled) return;
      settled = true;
      // The port is entangled with the worker, and one is opened per assessment.
      try {
        channel?.port1.close();
      } catch {
        /* already closed */
      }
      resolve(version);
    };

    try {
      channel = new MessageChannel();
      channel.port1.onmessage = (event: MessageEvent) => {
        const version = event.data?.version;
        done(typeof version === "string" ? version : null);
      };
      worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
      setTimeout(() => done(null), versionReplyTimeoutMs);
    } catch {
      // postMessage throws on a redundant worker. Same meaning: no answer.
      done(null);
    }
  });
}

const VERSION_SHAPE = /^v(\d+)(?:-([0-9a-z_]+))?$/i;

/**
 * Is `parked` an update over `running`? Generation compared as a number;
 * within a generation, a different build id is newer (see the header). An
 * unparseable version on either side falls back to plain inequality.
 */
export function isNewer(parked: string, running: string): boolean {
  const a = VERSION_SHAPE.exec(parked);
  const b = VERSION_SHAPE.exec(running);
  if (a && b) {
    const generationA = Number(a[1]);
    const generationB = Number(b[1]);
    if (generationA !== generationB) return generationA > generationB;
    return (a[2] ?? "") !== (b[2] ?? "");
  }
  return parked !== running;
}

/** Called by the UI once the user accepts the update (and by a resume). */
export function applyUpdate(): void {
  if (!waitingWorker) {
    // Nothing parked — it may have activated on its own (every other tab
    // closed). A plain reload is still the correct response.
    window.location.reload();
    return;
  }
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

function selfApply(): void {
  awayMs = 0;
  applyUpdate();
}

function announce(version: string): void {
  pendingVersion = version;
  // Found on a resume after a real absence: take it rather than asking.
  if (justResumed() && mayApplyWithoutAsking()) {
    selfApply();
    return;
  }
  updateHandler?.(version);
}

/**
 * Register a callback invoked when a genuinely newer version is waiting. The
 * version is passed so the UI can remember which one the user postponed.
 * Holds ONE handler — a second subscriber replaces the first.
 */
export function onUpdateReady(handler: UpdateHandler): void {
  updateHandler = handler;
  // A worker may already have been assessed before the UI mounted.
  if (pendingVersion) handler(pendingVersion);
}

/**
 * Decide whether a parked worker is worth interrupting the user for. `worker`
 * is passed explicitly by the `installed` path rather than read back off the
 * registration, so nothing here depends on event ordering.
 */
async function assessWaiting(
  registration: ServiceWorkerRegistration,
  worker: ServiceWorker | null = registration.waiting,
): Promise<void> {
  if (!worker || worker === assessed) return;
  assessed = worker;
  waitingWorker = worker;

  // Nothing controls this page (a hard reload, or the very first visit), so
  // there is no running version for the parked one to be an update TO.
  const running = navigator.serviceWorker.controller;
  if (!running) return;

  const [parkedVersion, runningVersion] = await Promise.all([askVersion(worker), askVersion(running)]);

  // Both silent: both predate the handshake. Say nothing.
  if (parkedVersion === null && runningVersion === null) return;

  // The parked worker answers and the running one does not: the running
  // worker is older than v11 by definition, so this IS an update.
  if (parkedVersion !== null && runningVersion === null) {
    announce(parkedVersion);
    return;
  }

  // The running worker answers and the parked one does not: the parked one is
  // the OLDER of the two. Leave it parked; activating it would be a downgrade.
  if (parkedVersion === null) return;

  if (isNewer(parkedVersion, runningVersion!)) {
    announce(parkedVersion);
    return;
  }

  if (parkedVersion === runningVersion) {
    // A zombie: the same version parked over itself. Activating it costs
    // nothing — same VERSION, same cache names, `activate` deletes nothing —
    // and clears a worker that would otherwise be re-detected for ever.
    silentSwap = true;
    worker.postMessage({ type: "SKIP_WAITING" });
  }

  // Anything left is a parked worker BEHIND the running one. Say nothing.
}

/* ── Enabled? ────────────────────────────────────────────────────────── */

/**
 * No worker in `npm run dev` unless VITE_SW_DEV=1.
 *
 * A worker caching a dev build helps nobody and makes HMR behave strangely.
 * The opt-in exists because web push cannot be exercised without a registered
 * worker, and localhost is a secure context, so a real subscription and a
 * real notification both work from a dev server when asked for.
 */
export function serviceWorkerEnabled(): boolean {
  if (forceEnabled !== null) return forceEnabled;
  return !(import.meta.env.DEV && import.meta.env.VITE_SW_DEV !== "1");
}

/** Fired on window when the worker says the push subscription changed. */
export const PUSH_RESYNC_EVENT = "salefish:push-resync";

/**
 * A worker registered by an earlier dev session keeps answering requests
 * until something unregisters it — "no worker in dev" has to mean none.
 */
function unregisterDevWorkers(): void {
  navigator.serviceWorker
    .getRegistrations?.()
    .then((registrations) => {
      for (const registration of registrations) {
        const worker = registration.active ?? registration.waiting ?? registration.installing;
        if (worker?.scriptURL.endsWith("/service-worker.js")) void registration.unregister();
      }
    })
    .catch(() => {
      /* nothing registered, or not allowed — either way nothing to do */
    });
}

export function registerServiceWorker(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (!serviceWorkerEnabled()) {
    unregisterDevWorkers();
    return;
  }

  // Registering after `load` keeps the worker's own requests off first paint.
  window.addEventListener("load", () => {
    hadController = !!navigator.serviceWorker.controller;

    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // A new worker already installed and waiting before this page loaded.
        void assessWaiting(registration);

        // A new worker found while this page is open.
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener("statechange", () => {
            // `controller` is null on the very first install — not an update,
            // just the worker arriving.
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              void assessWaiting(registration, installing);
            }
          });
        });

        // Ask on every load. The browser's own check is every 24 hours, which
        // is far too slow for an app deployed several times a day.
        registration.update?.().catch(() => {
          /* offline, or the script is unreachable — retry on resume */
        });

        /*
          AND ON EVERY RESUME — the only check an installed phone app gets.

          `visibilitychange` covers app-switching; `pageshow` with `persisted`
          covers a restore from the back-forward cache, which fires no `load`
          and no visibility change of its own on some engines. Throttled: iOS
          fires these liberally, and each check is a network request.
        */
        let lastCheck = Date.now();
        const onResume = (event?: Event) => {
          if (document.visibilityState !== "visible") {
            hiddenSince = Date.now();
            return;
          }
          const restored = event?.type === "pageshow" && (event as PageTransitionEvent).persisted === true;
          if (event?.type === "pageshow" && !restored) return; // an ordinary load, not a resume
          const now = Date.now();
          // A bfcache restore with no recorded hide was away for an unknown
          // time — long enough to have been put in the cache at all.
          awayMs = hiddenSince ? now - hiddenSince : restored ? AWAY_LONG_ENOUGH_MS : 0;
          hiddenSince = 0;
          resumedAt = now;

          /*
            An update already found — and postponed with "Later" — is still
            sitting in `waiting`, and no second `updatefound` will ever fire for
            it. Coming back after a real absence is the moment this module
            treats as safe to take one without asking, so take it.
          */
          if (
            awayMs >= AWAY_LONG_ENOUGH_MS &&
            pendingVersion &&
            waitingWorker &&
            navigator.serviceWorker.controller &&
            mayApplyWithoutAsking()
          ) {
            selfApply();
            return;
          }

          if (now - lastCheck < RESUME_CHECK_THROTTLE_MS) return;
          lastCheck = now;
          registration.update?.().catch(() => {
            /* retry on the next resume */
          });
        };
        /*
          AND EVERY 15 MINUTES WHILE IT STAYS OPEN AND ON SCREEN.

          Load and resume used to be the only checks. So an app kept in front of
          someone (a desk left on Console all day, a phone in use) never learned
          of a deploy while they worked, and the resume that finally did took it
          without asking: the "A new version is ready" card was never seen
          (Andrew, 2026-10-22). Found while they work, an update is announced
          as the card, which is what the card is for. Hidden, it waits for the
          resume, which checks anyway.
        */
        setInterval(() => {
          if (document.visibilityState !== "visible") return;
          const now = Date.now();
          if (now - lastCheck < RESUME_CHECK_THROTTLE_MS) return;
          lastCheck = now;
          registration.update?.().catch(() => {
            /* offline for now: the next interval or resume asks again */
          });
        }, OPEN_CHECK_MS);

        document.addEventListener("visibilitychange", () => onResume());
        window.addEventListener("pagehide", () => {
          hiddenSince = Date.now();
        });
        window.addEventListener("pageshow", (event) => onResume(event));
      })
      .catch((error) => {
        console.warn("[SW] registration failed:", error);
      });

    // The worker says the push subscription was rotated: let the page re-sync
    // it with its own session (lib/pushSync.ts listens for this).
    navigator.serviceWorker.addEventListener?.("message", (event: MessageEvent) => {
      if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
        window.dispatchEvent(new Event(PUSH_RESYNC_EVENT));
      }
    });

    // Fires when a waiting worker takes control after SKIP_WAITING.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // A FIRST install's claim, not an update. Anything after it is real.
      if (!hadController) {
        hadController = true;
        return;
      }
      // A silent same-version swap must not bounce the user out of what they
      // were doing: identical code, identical caches, nothing to reload for.
      if (silentSwap) {
        silentSwap = false;
        return;
      }
      // One reload per page lifetime, however many times this fires.
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

/**
 * Harness seam (client/harness/main.tsx, `?nudge=update`): behave as if a
 * genuinely newer worker were parked, so the update card can be reviewed with
 * no service worker at all. Never called by the app.
 */
export function __simulateUpdateReady(version: string): void {
  pendingVersion = version;
  updateHandler?.(version);
}

/**
 * Test seam: the module holds process-wide state a suite must be able to clear,
 * a two-second wait for a worker that is never going to answer, and a dev
 * gate that is always on under vitest.
 */
export function __resetForTests(opts?: { versionReplyTimeoutMs?: number; enabled?: boolean }): void {
  waitingWorker = null;
  assessed = null;
  pendingVersion = null;
  updateHandler = null;
  reloading = false;
  silentSwap = false;
  hadController = false;
  hiddenSince = 0;
  awayMs = 0;
  resumedAt = 0;
  forceEnabled = opts?.enabled ?? true;
  versionReplyTimeoutMs = opts?.versionReplyTimeoutMs ?? 2_000;
}
